/**
 * The keys of a caller-supplied map that hold an identity CREDENTIAL rather than
 * context — today the embed widget's HMAC `user_hash` — and the one filter that
 * removes them.
 *
 * 🔴 A `user_hash` IS A PERMANENT BEARER CREDENTIAL. It is
 * `HMAC-SHA256(identityVerificationSecret, externalUserId)` with no timestamp,
 * nonce or expiry, so whoever holds `(external_user_id, user_hash)` IS that
 * visitor to Nexus for as long as the deployment's secret exists. It is verified
 * on the request that carries it and has no business anywhere after that.
 *
 * ## One predicate, every surface
 *
 * The same question is asked wherever a caller's map can carry the credential,
 * so it is answered here, once, and every surface imports it:
 *
 *   - the agent's system prompt — `renderPromptMetadataBlock` never renders it,
 *     because a prompt goes to the LLM provider and into every stored trace;
 *   - `Chat.settings.embedData` — `CreateChatroomUseCase` drops it before the
 *     row is written;
 *   - Sentry — `sanitizePayload` redacts it from every request body an
 *     unhandled error ships, where `isCredentialKey` (which knows no `hash`
 *     name) would let it through;
 *   - the public v1 chat door — `SendChatMessageMetadataSchema` REFUSES it, so a
 *     caller is told rather than silently stripped.
 *
 * It lives in `@nexus/types/core` because that last surface is a contract in
 * this package and the others are backend code: one home both can import is
 * what keeps the door and the sinks from drifting into two vocabularies.
 *
 * ## Matched by NORMALISED spelling, not by one literal
 *
 * The map is whatever a browser sent, so the widget's `user_hash` is only the
 * spelling the shipped widget uses. The same value is `userHash` in the widget's
 * own JS, `x-user-hash` as a header and `identityHash` on the headless mint, and
 * any of those can be typed into the widget's `sdata` blob by a host page. So a
 * key is compared with case, `_` and `-` removed: `user_hash`, `userHash`,
 * `USER-HASH` and `identity_hash` are one key here. A key that merely CONTAINS
 * the word (`user_hash_note`) is not a credential and is kept — the set is of
 * whole names, so the filter cannot widen into ordinary context.
 */

import { normaliseKeySpelling } from "./key-spelling";

/** Credential names, in normalised form — see {@link normaliseKeySpelling}. */
const CREDENTIAL_KEYS: ReadonlySet<string> = new Set(["userhash", "identityhash"]);

/** True when `key` names an identity credential, in any of its spellings. */
export function isIdentityCredentialKey(key: string): boolean {
  return CREDENTIAL_KEYS.has(normaliseKeySpelling(key));
}

/**
 * `map` without its identity-credential entries.
 *
 * Returns the INPUT itself when it holds none, so a caller writing the result
 * back changes nothing for every ordinary map — a no-op by identity.
 */
export function withoutIdentityCredentials<V>(map: Record<string, V>): Record<string, V> {
  const keys = Object.keys(map);
  if (!keys.some(isIdentityCredentialKey)) return map;

  const kept: Record<string, V> = {};
  for (const key of keys) {
    if (!isIdentityCredentialKey(key)) kept[key] = map[key];
  }
  return kept;
}
